/**
 * 精灵携带的可变 Buff 数据。
 *
 * Buff 实例属于单只精灵，不应在多个精灵之间共享。层数统一由该类
 * 做边界处理，Pet 在层数变化后负责通知具体魂印刷新效果。
 */
export class Buff {
    /**
     * stacks，保存当前实例的stacks状态。
     */
    public stacks: number;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param name 名称
     * @param description description参数，参与本次操作；默认值为 `""`
     * @param stacks stacks参数，参与本次操作；默认值为 `0`
     * @param maxStacks maxStacks参数，参与本次操作；默认值为 `Number.POSITIVE_INFINITY`
     */
    constructor(
        public readonly name: string,
        public readonly description: string = "",
        stacks: number = 0,
        public readonly maxStacks: number = Number.POSITIVE_INFINITY,
    ) {
        if (Number.isNaN(maxStacks) || maxStacks < 0) {
            throw new RangeError("Buff 最大层数必须是非负数值");
        }
        this.stacks = Math.max(0, Math.min(maxStacks, Math.floor(stacks)));
    }

    /**
     * 读取当前层数。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 层数() {
        return this.stacks;
    }

    /**
     * 更新当前层数。
     * @param value 待处理的值
     */
    set 层数(value: number) {
        this.stacks = this.clamp(value);
    }

    /**
     * tickDurations：执行对应的领域操作。
     */
    tickDurations() {}

    /**
     * addStacks：执行对应的领域操作。
     * @param amount amount参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    addStacks(amount: number): number {
        if (!Number.isFinite(amount)) {
            throw new TypeError("Buff 层数变化必须是有限数值");
        }
        /**
         * previous，供本模块相关规则使用。
         */
        const previous = this.stacks;
        this.stacks = this.clamp(this.stacks + amount);
        return this.stacks - previous;
    }

    /**
     * removeStacks：执行对应的领域操作。
     * @param amount amount参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    removeStacks(amount: number): number {
        if (!Number.isFinite(amount)) {
            throw new TypeError("Buff 层数变化必须是有限数值");
        }
        return this.addStacks(-Math.max(0, amount));
    }

    /**
     * clear：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    clear() {
        /**
         * removed，供本模块相关规则使用。
         */
        const removed = this.stacks;
        this.stacks = 0;
        return removed;
    }

    /**
     * clamp：执行对应的领域操作。
     * @param value 待处理的值
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private clamp(value: number) {
        if (!Number.isFinite(value)) {
            throw new TypeError("Buff 层数必须是有限数值");
        }
        return Math.max(0, Math.min(this.maxStacks, Math.floor(value)));
    }
}

/** 重生之翼魂印中的神耀能量，最多储存 6 层。 */
export class 神耀能量 extends Buff {
    /** 无上天命剑命中后，在限定回合内击败对手可保留的层数。 */
    defeatRetention: number = 0;
    /**
     * defeatRetentionRounds，保存当前实例的defeatRetentionRounds状态。
     */
    defeatRetentionRounds: number = 0;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param stacks stacks参数，参与本次操作；默认值为 `0`
     */
    constructor(stacks: number = 0) {
        super("神耀能量", "每储存 1 层神耀能量，重生之翼获得对应的魂印效果。", stacks, 6);
    }

    /**
     * grantDefeatRetention：执行对应的领域操作。
     * @param stacks stacks参数，参与本次操作
     * @param rounds rounds参数，参与本次操作
     */
    grantDefeatRetention(stacks: number, rounds: number) {
        this.defeatRetention = Math.max(0, Math.floor(stacks));
        this.defeatRetentionRounds = Math.max(0, Math.floor(rounds));
    }

    /**
     * tickDurations：执行对应的领域操作。
     */
    override tickDurations() {
        this.passRetentionRound();
    }

    /**
     * passRetentionRound：执行对应的领域操作。
     */
    passRetentionRound() {
        if (this.defeatRetentionRounds > 0) {
            this.defeatRetentionRounds -= 1;
        }
        if (this.defeatRetentionRounds === 0) {
            this.defeatRetention = 0;
        }
    }
}

/** 创建独立的神耀能量实例，供 Pet/快照使用。 */
export const 创建神耀能量 = (stacks: number = 0) => new 神耀能量(stacks);

/** 兼容需要直接实例化 Buff 对象的调用方。 */
export const 神耀能量对象 = 创建神耀能量();
